import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHaptics } from '@/hooks/useHaptics';
import { type Egg, eggLineKey } from '@/lib/eggs';
import { cn } from '@/lib/cn';

/**
 * L'uovo del Profilo: galleggia, ogni tanto si scuote, e reagisce al tocco.
 *
 * Serve a dire che lì dentro c'è qualcosa di vivo che deve ancora arrivare. Un
 * uovo fermo sarebbe un segnaposto; uno che si muove è una promessa.
 *
 * Tre animazioni su tre elementi ANNIDATI, una per elemento:
 *  - il contenitore galleggia (costante),
 *  - il guscio rimbalza al tocco (a comando, una volta),
 *  - l'immagine si scuote (saltuaria, ciclo lungo).
 * Sullo stesso elemento andrebbero fuse in un keyframe unico, e il rimbalzo —
 * che parte a comando — costringerebbe a riscriverlo a ogni tocco azzerando il
 * punto in cui le altre due si trovano.
 */

/** Onomatopee: sono suoni, funzionano uguale nelle due lingue — non tradotte. */
const SOUNDS = ['toc toc…', 'crick…', '…tuc', 'scrick!', '…?', 'tap tap…'] as const;

/** Quanto resta un verso prima del successivo. */
const SOUND_MS = 2600;

/** Quanto resta il fumetto se non lo si tocca di nuovo. */
const BUBBLE_MS = 4200;

/**
 * Tre frasi valgono per ogni uovo; la quarta parla del proprio colore e vive
 * nella variante (`egg_line.<id>`).
 */
const GENERIC_LINES = 3;
const LINES = GENERIC_LINES + 1;

/** Quanto dura l'uscita del fumetto. Deve combaciare con `egg-bubble-out`. */
const EXIT_MS = 160;

export function HatchingEgg({ egg }: { egg: Egg }) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const [sound, setSound] = useState(0);

  // `at` è la quota (in percentuale dell'altezza) del punto toccato: il fumetto
  // esce da lì e non da un posto fisso, così sembra che risponda al dito invece
  // di comparire per conto suo.
  // `alto` = si è toccata la metà superiore, quindi il fumetto sta SOPRA quel
  // punto e la codina guarda in giù.
  const [bubble, setBubble] = useState<{
    line: number;
    at: number;
    alto: boolean;
    id: number;
  } | null>(null);
  // Fase d'uscita: il fumetto è ancora montato ma sta svanendo. Senza questo
  // stato React lo smonterebbe subito e l'animazione non avrebbe il tempo di
  // partire — smontare è istantaneo, un'animazione no.
  const [closing, setClosing] = useState(false);
  const nextLine = useRef(0);
  const bubbleTimer = useRef<number | null>(null);
  const exitTimer = useRef<number | null>(null);
  const shellRef = useRef<HTMLSpanElement | null>(null);
  const eggRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setSound((n) => (n + 1) % SOUNDS.length), SOUND_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(
    () => () => {
      if (bubbleTimer.current) window.clearTimeout(bubbleTimer.current);
      if (exitTimer.current) window.clearTimeout(exitTimer.current);
    },
    [],
  );

  /** Avvia l'uscita; lo smontaggio arriva a fine animazione. */
  const closeBubble = () => {
    if (bubbleTimer.current) window.clearTimeout(bubbleTimer.current);
    setClosing(true);
    exitTimer.current = window.setTimeout(() => {
      setBubble(null);
      setClosing(false);
    }, EXIT_MS);
  };

  /**
   * Toccando altrove il fumetto si chiude.
   *
   * In fase di CATTURA e su `pointerdown`, non sul click: così sparisce appena
   * il dito tocca, e non dopo il rilascio — altrimenti toccando un comando
   * sotto si vedrebbe il fumetto restare mentre la schermata cambia.
   *
   * L'uovo è escluso: lì il tocco ha già il suo effetto, cioè passare alla
   * frase successiva. Senza questa eccezione i due gestori si annullerebbero a
   * vicenda e il fumetto non cambierebbe mai.
   */
  useEffect(() => {
    if (!bubble || closing) return;
    const onDown = (e: PointerEvent) => {
      if (eggRef.current?.contains(e.target as Node)) return;
      closeBubble();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [bubble, closing]);

  const onTap = (e: React.MouseEvent<HTMLButtonElement>) => {
    haptics.impact('light');

    // Riavvio del rimbalzo togliendo la classe, forzando un reflow e
    // rimettendola: ri-assegnare la stessa animazione senza questo passaggio
    // non la fa ripartire, e un tocco su due resterebbe senza risposta.
    const shell = shellRef.current;
    if (shell) {
      shell.classList.remove('egg-bounce');
      void shell.offsetWidth;
      shell.classList.add('egg-bounce');
    }

    const box = e.currentTarget.getBoundingClientRect();
    const at = ((e.clientY - box.top) / box.height) * 100;

    // Toccando di nuovo il fumetto non si chiude e basta: passa al successivo.
    // Chiuderlo e richiederne un altro sarebbe due tocchi per una cosa sola.
    const line = nextLine.current;
    nextLine.current = (line + 1) % LINES;
    // La codina punta SEMPRE al punto toccato, quindi il fumetto si sposta
    // dalla parte opposta: tocchi in alto e il fumetto sale, con la codina
    // sotto che indica in giù. Il fumetto non copre mai il punto toccato — cioè
    // il dito — che è l'unica ragione per cui vale la pena spostarlo.
    const alto = at < 50;
    // Un fumetto che stava uscendo va riportato in vita: toccando durante
    // l'uscita ci si aspetta il successivo, non di aspettare che il primo
    // finisca di svanire.
    if (exitTimer.current) window.clearTimeout(exitTimer.current);
    setClosing(false);
    setBubble({
      line,
      at: Math.min(82, Math.max(22, at)),
      alto,
      id: Date.now(),
    });

    if (bubbleTimer.current) window.clearTimeout(bubbleTimer.current);
    bubbleTimer.current = window.setTimeout(closeBubble, BUBBLE_MS);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3">
      <div className="relative flex min-h-0 w-full flex-1 items-center justify-center">
        <button
          ref={eggRef}
          type="button"
          onClick={onTap}
          aria-label={t('egg.aria', { defaultValue: 'Mysterious egg' })}
          // `select-none` e `touch-callout` spenti: dentro la WebView di Telegram
          // una pressione lunga su un'immagine apre il menu di sistema («salva
          // immagine»), che qui è un'intrusione — l'uovo è un elemento dell'app,
          // non una figura in una pagina.
          className="egg-float flex h-full w-full select-none items-center justify-center [-webkit-touch-callout:none] [animation:egg-float_3.2s_ease-in-out_infinite]"
        >
          <span
            ref={shellRef}
            // Nessuna proprietà d'animazione qui: durata, curva e keyframe
            // stanno tutti nella regola `.egg-bounce` in globals.css, che il
            // tocco accende e spegne. Dichiararli anche qui li lascerebbe attivi
            // pure a classe rimossa, e `animation-name: none` scritto in linea
            // rischia di vincere sulla regola a seconda dell'ordine dei fogli.
            className="flex h-full w-full items-center justify-center"
          >
            {/* Due elementi annidati e non uno: lo scuotimento è una
                trasformazione, la tinta della variante è un filtro, e su un solo
                elemento il filtro dell'arcobaleno — che è un'animazione —
                sovrascriverebbe quello statico delle altre varianti. */}
            {/* `max-h-[88%]` e non `max-h-full`: da quando la chrome del Profilo
                è una targa di cuoio, l'uovo ha sotto di sé una distesa vuota di
                sfondo e riempirla tutta lo faceva sembrare incastrato fra la
                targa e la barra. Un po' più piccolo si vede che GALLEGGIA — che
                è quello che l'animazione sta già raccontando. */}
            <span
              className={cn('flex max-h-[88%]', egg.filterClass)}
              style={{ filter: egg.filter }}
            >
              <img
                src="/egg.png"
                alt=""
                aria-hidden
                className="egg-wobble max-h-full w-auto object-contain [animation:egg-wobble_7s_ease-in-out_infinite] [transform-origin:50%_85%]"
              />
            </span>
          </span>
        </button>

        {bubble && (
          // `key` sull'id: toccando di nuovo mentre il fumetto è aperto,
          // l'elemento si rimonta e l'animazione d'ingresso riparte. Senza,
          // cambierebbe solo il testo e il fumetto sembrerebbe non aver
          // reagito al secondo tocco.
          <div
            key={bubble.id}
            // L'ombra sta QUI, sul contenitore, ed è una `drop-shadow` e non una
            // `box-shadow`. Le due ombre separate sui due pezzi si proiettavano
            // l'una sull'altra: quella del corpo cadeva sulla codina sottostante
            // e la sporcava di grigio, rivelando che erano due forme. Una
            // `drop-shadow` sul genitore segue la SAGOMA di tutto il gruppo,
            // quindi corpo e codina proiettano un'ombra sola e si leggono come
            // un pezzo unico di bianco.
            className={cn(
              'pointer-events-none absolute left-1/2 z-10 w-[min(17rem,80%)] -translate-x-1/2 drop-shadow-xl',
              closing
                ? 'egg-bubble [animation:egg-bubble-out_160ms_ease-in_forwards]'
                : 'egg-bubble [animation:egg-bubble_240ms_ease-out]',
            )}
            // Ancorato per il BORDO che tocca il dito: quando il fumetto sta
            // sopra è il suo bordo inferiore a fermarsi lì (`bottom`), quando
            // sta sotto è quello superiore (`top`). Usando sempre `top` il
            // fumetto alto coprirebbe il punto toccato, perché crescerebbe
            // verso il basso a partire da lì.
            style={bubble.alto ? { bottom: `${100 - bubble.at}%` } : { top: `${bubble.at}%` }}
          >
            {/* Bianco con testo verde acqua scuro, e NON la solita superficie
                teal col bordo chiaro: è un fumetto, cioè qualcuno che parla —
                deve staccarsi dall'interfaccia invece di somigliarle. Niente
                `card-edge` qui: quel bordo è una luce pensata per superfici
                scure e su bianco sparirebbe. */}
            {/* La codina: un quadrato ruotato e non il classico triangolo di
                bordi, che sarebbe una forma senza sfondo — quindi non si
                tingerebbe col corpo se un giorno il fumetto smettesse di essere
                bianco pieno. Sta SOTTO il testo nell'ordine di sovrapposizione
                (`-z-10`) così il suo spigolo non taglia la prima riga.
                Senza ombra propria: l'ombra è una sola, sul contenitore. */}
            <span
              aria-hidden
              className={cn(
                'absolute left-1/2 -z-10 h-4 w-4 -translate-x-1/2 rotate-45 rounded-[3px] bg-white',
                bubble.alto ? '-bottom-1.5' : '-top-1.5',
              )}
            />
            <p className="relative rounded-2xl bg-white px-4 py-3 text-center text-sm font-semibold leading-snug text-bg">
              {/* Le prime tre frasi valgono per ogni uovo; l'ultima parla del
                  proprio colore, quindi la chiede alla variante. Lasciata fra
                  le generiche, a chi ha l'uovo blu comparirebbe un commento
                  entusiasta sul rosso. */}
              {bubble.line < GENERIC_LINES
                ? t(`egg.line_${bubble.line}`, { defaultValue: FALLBACK_LINES[bubble.line] })
                : t(eggLineKey(egg.id), { defaultValue: FALLBACK_LINES[0] })}
            </p>
          </div>
        )}
      </div>

      {/*
        `key` sul verso: rimonta l'elemento a ogni cambio, e senza rimontaggio
        l'animazione d'ingresso partirebbe una volta sola — il primo suono
        comparirebbe in dissolvenza e tutti i successivi di scatto.

        Altezza fissa perché versi di lunghezza diversa non facciano sobbalzare
        l'uovo sopra a ogni cambio.
      */}
      <p className="flex h-6 items-center text-sm font-semibold text-fg-muted" aria-live="off">
        <span key={sound} className="egg-sound [animation:egg-sound_2.6s_ease-in-out]">
          {SOUNDS[sound]}
        </span>
      </p>
    </div>
  );
}

/** Testi di riserva se la chiave di locale manca. */
const FALLBACK_LINES = [
  'Un uovo misterioso… si schiuderà?',
  'Chissà a cosa serve quest’uovo, è un mistero!',
  'Sento una strana energia provenire dall’uovo… magari è solo la mia impressione.',
];
